import { useEffect, useRef, useState } from "react";
import { CalendarPlus, ChevronDown, FileText, Lock, MapPin } from "lucide-react";
import { cn } from "@/lib/cn";
import { useClient360Meetings } from "@/hooks/useClient360Meetings";
import { ScheduleMeetingDrawer } from "./ScheduleMeetingDrawer";
import { MeetingReportDrawer, type ReportTarget } from "./MeetingReportDrawer";
import { ReportRequiredBanner } from "@/components/meetings/ReportRequiredBanner";
import type { Client360AllowedActions } from "@/api/clients";

/**
 * 3C — meeting CTAs on the «Історія» tab: «Запланувати зустріч» · «Запланувати
 * візит 🔒» (backend-gated, Этап 5) · «Заповнити звіт». All gating is
 * backend-owned via allowedActions (AC-8); the frontend never infers visit
 * availability. The report CTA picks a meeting awaiting a report; the timeline's
 * inline «Заповнити звіт →» drives the same drawer via openReportSignal.
 */
export function MeetingActionBar({
  holdingId,
  aa,
  openReportSignal,
}: {
  holdingId: string;
  aa: Client360AllowedActions;
  /** External trigger from a timeline event's inline «Заповнити звіт →». */
  openReportSignal?: { interactionId: string; summary: string; nonce: number } | null;
}) {
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [reportTarget, setReportTarget] = useState<ReportTarget | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  const canSchedule = !!aa.canScheduleMeeting;
  const canVisit = !!aa.canScheduleVisit;
  const canReport = !!aa.canFillReport;

  // S4: список вантажиться одразу — ReportRequiredBanner (екран 2) потребує
  // report-required стан без відкриття пікера.
  const meetingsQ = useClient360Meetings(holdingId, true);
  const reportable = (meetingsQ.data?.items ?? []).filter((m) => m.allowedActions.canFillReport);
  const requiredItem = (meetingsQ.data?.items ?? []).find((m) => m.reportRequired) ?? null;

  useEffect(() => {
    if (!pickerOpen) return;
    const onClick = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) setPickerOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [pickerOpen]);

  // Timeline inline «Заповнити звіт →».
  useEffect(() => {
    if (!openReportSignal) return;
    setReportTarget({ interactionId: openReportSignal.interactionId, summary: openReportSignal.summary });
  }, [openReportSignal]);

  return (
    <>
      {/* D-055 S4 — Report Required state на 3C surface (visual екран 2). */}
      {requiredItem && (
        <div className="mb-3">
          <ReportRequiredBanner
            summary={requiredItem.summary}
            occurredAt={requiredItem.occurredAt}
            slaDueAt={requiredItem.reportSlaDueAt}
            overdueSince={requiredItem.reportOverdueSince}
            canFill={requiredItem.allowedActions.canFillReport}
            onFill={() =>
              setReportTarget({ interactionId: requiredItem.id, summary: requiredItem.summary })
            }
          />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {canSchedule && (
          <button
            type="button"
            onClick={() => setScheduleOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-sm font-medium hover:bg-muted"
          >
            <CalendarPlus className="h-4 w-4" aria-hidden /> Запланувати зустріч
          </button>
        )}

        {/* Visit — backend-gated (Этап 5). Always rendered disabled with reason. */}
        <button
          type="button"
          disabled={!canVisit}
          aria-disabled={!canVisit}
          title={canVisit ? undefined : (aa.visitDisabledReason ?? "Доступно після Этапу 5")}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-sm font-medium",
            canVisit ? "hover:bg-muted" : "cursor-not-allowed text-muted-foreground opacity-50",
          )}
        >
          <MapPin className="h-4 w-4" aria-hidden /> Запланувати візит
          {!canVisit && <Lock className="h-3.5 w-3.5" aria-hidden />}
        </button>

        {canReport && (
          <div className="relative" ref={pickerRef}>
            <button
              type="button"
              onClick={() => setPickerOpen((v) => !v)}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
              aria-haspopup="menu"
              aria-expanded={pickerOpen}
            >
              <FileText className="h-4 w-4" aria-hidden /> Заповнити звіт <ChevronDown className="h-3.5 w-3.5" aria-hidden />
            </button>
            {pickerOpen && (
              <div role="menu" className="absolute right-0 z-20 mt-1 w-72 rounded-lg border bg-background p-1.5 shadow-lg">
                <div className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Оберіть зустріч</div>
                {meetingsQ.isPending ? (
                  <div className="px-2 py-3 text-center text-xs text-muted-foreground">Завантаження…</div>
                ) : reportable.length === 0 ? (
                  <div className="px-2 py-3 text-center text-xs text-muted-foreground">Немає зустрічей для звіту.</div>
                ) : (
                  reportable.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setPickerOpen(false);
                        setReportTarget({ interactionId: m.id, summary: m.summary });
                      }}
                      className="flex w-full flex-col items-start gap-0.5 rounded-md px-2 py-2 text-left hover:bg-primary/10"
                    >
                      <span className="text-sm font-medium">{m.summary}</span>
                      <span className="text-[11.5px] text-muted-foreground">
                        {m.occurredAt ? fmtDate(m.occurredAt) : ""}{m.reportRequired ? " · звіт очікується" : ""}
                      </span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <ScheduleMeetingDrawer
        open={scheduleOpen}
        onClose={() => setScheduleOpen(false)}
        holdingId={holdingId}
        canScheduleVisit={canVisit}
        visitDisabledReason={aa.visitDisabledReason}
      />
      <MeetingReportDrawer
        target={reportTarget}
        holdingId={holdingId}
        onClose={() => setReportTarget(null)}
      />
    </>
  );
}

function fmtDate(iso: string): string {
  const d = new Date(iso.replace(" ", "T") + (iso.includes("Z") || iso.includes("+") ? "" : "Z"));
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString("uk-UA", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}
